import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * Every fill here is paired with the foreground token that is *guaranteed* to
 * read on it. That pairing is the whole point: the 2.94:1 buttons this replaces
 * were not a bad colour choice, they were a good colour with `text-white` bolted
 * on beside it, in twenty-odd files, with nothing to notice the mismatch.
 *
 * `primary` is aubergine rather than marigold. Marigold under white text is
 * 2.94:1 and cannot be a primary action on a light ground — see decisions.md 9.1.
 */
const button = cva(
  [
    "inline-flex items-center justify-center gap-2 rounded-md font-medium",
    "transition-colors outline-none",
    "focus-visible:ring-2 focus-visible:ring-[var(--accent-ring)] focus-visible:ring-offset-2",
    "focus-visible:ring-offset-[var(--surface-0)]",
    "disabled:pointer-events-none disabled:opacity-60",
  ],
  {
    variants: {
      variant: {
        /** The default action. 14.9:1. */
        primary: "bg-[var(--ink-9)] text-[var(--text-inverse)] hover:bg-[var(--ink-10)]",
        /** Accent-forward CTA — the derived shade, never the raw accent. */
        accent:
          "bg-[var(--accent-strong)] text-[var(--accent-foreground)] hover:bg-[var(--accent-strong-hover)]",
        secondary:
          "border border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--text-primary)] hover:bg-[var(--surface-3)]",
        ghost: "text-[var(--text-primary)] hover:bg-[var(--surface-3)]",
        link: "text-[var(--accent-ink)] underline underline-offset-4 hover:text-[var(--accent-strong)]",
        danger: "bg-[var(--danger)] text-[var(--text-inverse)] hover:brightness-110",
      },
      size: {
        sm: "h-8 px-3 text-xs",
        base: "h-10 px-4 text-sm",
        lg: "h-12 px-6 text-base",
        /** Square. Requires an aria-label — enforced by the type below. */
        icon: "h-10 w-10 p-0",
      },
      pill: { true: "rounded-full", false: "" },
    },
    defaultVariants: { variant: "primary", size: "base", pill: false },
  },
);

type ButtonVariants = VariantProps<typeof button>;
type Size = NonNullable<ButtonVariants["size"]>;

/**
 * An icon-only button renders no text, so it needs an accessible name. Making
 * that a discriminated union turns a missing label into a `tsc --noEmit` failure
 * — already a gate stage — rather than something a reviewer has to remember.
 */
type SizeConstraint =
  { size?: Exclude<Size, "icon">; "aria-label"?: string } | { size: "icon"; "aria-label": string };

export type ButtonProps = Omit<React.ComponentPropsWithoutRef<"button">, "aria-label"> &
  Omit<ButtonVariants, "size"> &
  SizeConstraint;

export function Button({ variant, size, pill, className, ...props }: ButtonProps) {
  return (
    <button
      className={cn(button({ variant, size, pill }), className)}
      data-icon-only={size === "icon" ? "" : undefined}
      {...props}
    />
  );
}

/** Same surface treatment for anchors, which cannot be <button>. */
export type ButtonLinkProps = Omit<React.ComponentPropsWithoutRef<"a">, "aria-label"> &
  Omit<ButtonVariants, "size"> &
  SizeConstraint;

/** Class string for anchors, which cannot be a <button>. */
export function buttonClasses(variants: ButtonVariants = {}) {
  return button(variants);
}
